import { CircularProgress } from '@mui/material';
import { Suspense, lazy } from 'react';
import { RequireRevRec } from './RequireRevRec';

const RevRecHomePage = lazy(() => import('../features/home/pages/RevRecHomePage'));
const CenterDashboardPage = lazy(() => import('../features/centerDashboard/pages/CenterDashboardPage'));
const PeriodSummaryPage = lazy(() => import('../features/periodSummary/pages/PeriodSummaryPage'));
const AccountDetailPage = lazy(() => import('../features/accountDetail/pages/AccountDetailPage'));
// The page's own path, as the four above, and NOT the feature's `index.ts`: that barrel is what
// `AccountDetailPage` imports the Out of period tab from, so a page re-exported there is loaded by
// every account-detail suite and drags the app entry point in behind `CenterPill`. The feature's
// `index.ts` records the same reason. This adds a fifth `no-private-feature-*` baseline entry beside
// the four the sibling pages already hold.
const OutOfPeriodPage = lazy(() => import('../features/outOfPeriod/pages/OutOfPeriodPage'));
// The page's own path, like all five above, and baselined with them. This element was the barrel's
// only consumer until the out-of-period page's Download template button became its second: a barrel
// that re-exports the page hands every sibling importer the whole wizard, because nothing marks this
// workspace side-effect-free, so the static re-export is a hard graph edge webpack cannot drop.
const AdjustmentUploadPage = lazy(() => import('../features/adjustmentUpload/pages/AdjustmentUploadPage'));
// Through the feature's `index.ts`, unlike the pages above: that barrel's only other importer reaches
// it with `import type`, which Babel erases, so no suite evaluates this barrel's module graph. The
// exception `OutOfPeriodPage` takes does not apply here — see the pending-approvals feature's own
// `index.ts` for why.
const PendingApprovalsPage = lazy(() =>
  import('../features/pendingApprovals').then((m) => ({ default: m.PendingApprovalsPage })));

/**
 * Lazy route element for the revenue recognition home screen. Authorization (the RevRec claim) is
 * enforced by the route-level `RequireRevRec` guard, which mounts `RevRecHomePage` — and its data
 * hooks — only for an authorized user, so no content renders and no period query fires before the
 * guard evaluates (US1, spec 179, T014).
 */
export function RevRecHomeRouteElement() {
  return (
    <RequireRevRec>
      <Suspense fallback={<CircularProgress data-testid="revrec-suspense-fallback" />}>
        <RevRecHomePage />
      </Suspense>
    </RequireRevRec>
  );
}

/**
 * Lazy route element for one center's month dashboard. Same `RequireRevRec` guard as the home route
 * — the page and its data hooks mount only for an authorized user, so nothing renders and no center
 * period query fires before the guard evaluates.
 */
export function CenterDashboardRouteElement() {
  return (
    <RequireRevRec>
      <Suspense fallback={<CircularProgress data-testid="revrec-suspense-fallback" />}>
        <CenterDashboardPage />
      </Suspense>
    </RequireRevRec>
  );
}

/**
 * Lazy route element for one center month's account summary. The same `RequireRevRec` guard mounts
 * the page and its data hooks only for an authorized user, so no period query fires before the guard
 * evaluates.
 */
export function PeriodSummaryRouteElement() {
  return (
    <RequireRevRec>
      <Suspense fallback={<CircularProgress data-testid="revrec-suspense-fallback" />}>
        <PeriodSummaryPage />
      </Suspense>
    </RequireRevRec>
  );
}

/**
 * Lazy route element for one account's month detail. The same `RequireRevRec` guard mounts the page
 * and its data hooks only for an authorized user, so no account query fires before the guard
 * evaluates.
 */
export function AccountDetailRouteElement() {
  return (
    <RequireRevRec>
      <Suspense fallback={<CircularProgress data-testid="revrec-suspense-fallback" />}>
        <AccountDetailPage />
      </Suspense>
    </RequireRevRec>
  );
}

/**
 * Lazy route element for one center month's out-of-period adjustments. The same `RequireRevRec` guard
 * mounts the page and its data hooks only for an authorized user, so no adjustments query fires before
 * the guard evaluates. The Out of period TAB is not routed — it is a panel inside the account detail
 * page, which this guard already covers.
 */
export function OutOfPeriodRouteElement() {
  return (
    <RequireRevRec>
      <Suspense fallback={<CircularProgress data-testid="revrec-suspense-fallback" />}>
        <OutOfPeriodPage />
      </Suspense>
    </RequireRevRec>
  );
}

/**
 * Lazy route element for the pending approvals screen — all centers, not one center's month, so it
 * mounts outside the center-scoped routes above. The same `RequireRevRec` guard mounts the page and
 * its data hooks only for an authorized user, so no approvals query fires before the guard evaluates.
 */
export function PendingApprovalsRouteElement() {
  return (
    <RequireRevRec>
      <Suspense fallback={<CircularProgress data-testid="revrec-suspense-fallback" />}>
        <PendingApprovalsPage />
      </Suspense>
    </RequireRevRec>
  );
}

/**
 * Lazy route element for the bulk adjustment upload wizard, registered on BOTH upload paths: the
 * page reads the fixed center from `useParams`, so a second element would differ from this one in
 * nothing. The same `RequireRevRec` guard mounts the page and its data hooks only for an authorized
 * user. There is no separate upload permission — the RevRec claim is the whole gate here, and the
 * foundation's server-side guard holds the endpoints behind the same claim.
 */
export function AdjustmentUploadRouteElement() {
  return (
    <RequireRevRec>
      <Suspense fallback={<CircularProgress data-testid="revrec-suspense-fallback" />}>
        <AdjustmentUploadPage />
      </Suspense>
    </RequireRevRec>
  );
}
